import { z } from 'zod/v4';
import { zodBigNumber } from '~/schemas/custom-schemas';
import {
  carbonContentErrorEnum,
  carbonContentUnitEnum,
  countryEnum,
  matchingResolutionEnum,
  subsidyRegimeEnum,
  technologyEnum,
} from '~/schemas/enums';
import { gridData } from '~/schemas/grid';
import { ALLOCATED_SUPPLY_TAG_PREFIX, SUPPLY_TAG_PREFIX } from '~/utils/tags';
import { dealTrancheVolumeTypeEnum } from './deals.schemas';
import {
  createMatchingAndPlottingResolutionGroups,
  createMetricsTableRequest,
  createMetricsTableResponse,
  metricsControlKeysEnum,
  nestedStringArrayFilter,
  periodStartFilter,
  stringArrayFilter,
  stringValueFilter,
} from './metrics.schemas';

const demandFilters = z.object({
  certificate_label: nestedStringArrayFilter.optional(),
  certificate_type: stringArrayFilter.optional(),
  counterparty_country: stringArrayFilter.optional(),
  counterparty_id: stringArrayFilter.optional(),
  country: nestedStringArrayFilter.optional(),
  deal_id: stringArrayFilter.optional(),
  deal_tranche_id: stringArrayFilter.optional(),
  deal_tranche_volume_type: stringArrayFilter.optional(),
  demand_contract_type: stringArrayFilter.optional(),
  demand_duration: stringValueFilter.optional(),
  matching_res: matchingResolutionEnum.optional(), // TODO: Move this into appropirate schema
  period_start: periodStartFilter,
  production_device_id: stringArrayFilter.optional(),
  sub_national_region: nestedStringArrayFilter.optional(),
  supra_national_region: nestedStringArrayFilter.optional(),
  technology: nestedStringArrayFilter.optional(),
});

export type DemandFilters = z.infer<typeof demandFilters>;
export type DemandFilterKeys = keyof typeof demandFilters.shape;

export const demandFilterKeys = Object.keys(demandFilters.shape) as Array<DemandFilterKeys>;

export const demandFilterHashKeysMap = {
  groups: metricsControlKeysEnum.enum.breakdown_by,
};

export const demandBreakdownByEnum = z.enum(['technology', 'prod_device', 'country', 'sub_national_region']);
export type FixedDemandBreakdownBy = z.infer<typeof demandBreakdownByEnum>;
export type SupplyTagDemandBreakdownBy = `${typeof SUPPLY_TAG_PREFIX}${string}`;
export type DemandBreakdownBy = FixedDemandBreakdownBy | SupplyTagDemandBreakdownBy;

export const DEFAULT_DEMAND_BREAKDOWN_BY: FixedDemandBreakdownBy = demandBreakdownByEnum.enum.technology;

export function isSupplyTagDemandBreakdownBy(
  breakdownBy: DemandBreakdownBy,
): breakdownBy is SupplyTagDemandBreakdownBy {
  return breakdownBy.startsWith(SUPPLY_TAG_PREFIX);
}

export const DEMAND_COMPARE_PREFIX = 'demandCompare:';
const demandTableLabel = z.union([
  z.literal('demandMetricsSummary'),
  z.literal('demandDurations'),
  z.literal('demandMatchingOverTime'),
  z.literal('demandMatchingAverage'),
  z.literal(`${DEMAND_COMPARE_PREFIX}five_minute`),
  z.literal(`${DEMAND_COMPARE_PREFIX}fifteen_minute`),
  z.literal(`${DEMAND_COMPARE_PREFIX}thirty_minute`),
  z.literal(`${DEMAND_COMPARE_PREFIX}hourly`),
  z.literal(`${DEMAND_COMPARE_PREFIX}daily`),
  z.literal(`${DEMAND_COMPARE_PREFIX}monthly`),
  z.literal(`${DEMAND_COMPARE_PREFIX}quarterly`),
  z.literal(`${DEMAND_COMPARE_PREFIX}yearly`),
  z.literal(`${DEMAND_COMPARE_PREFIX}yearly_from_april`),
  z.literal(`${DEMAND_COMPARE_PREFIX}yearly_from_may`),
  z.literal('pivotTable'),
]);
const demandColumns = z.union([
  // Groups
  z.literal('period_start'),
  z.literal('duration'),
  z.literal('counterparty_client_reference'),
  z.literal('counterparty_name'),
  z.literal('technology'),
  z.literal('country'),
  z.literal('supra_national_region'),
  z.literal('sub_national_region'),
  z.literal('certificate_label'),
  z.literal('certificate_type'),
  z.literal('subsidy_regime'),
  z.literal('deal_client_reference'),
  z.literal('deal_tranche_volume_type'),
  z.literal('production_device_name'),
  z.literal('production_device_client_reference'),
  z.literal('production_device_registry_identifier'),
  z.literal('demand_contract_type'),
  z.literal('max_delivery_date_year_and_month'),
  z.string().regex(/^tag_.+$/),
  // Metrics
  z.literal('demand'),
  z.literal('delivered_demand'),
  z.literal('supply_allocated_to_demand'),
  z.literal('supply_allocated_matching_demand'),
  z.literal('supply_allocated_in_excess_of_demand'),
  z.literal('metered_demand'),
  z.literal('contracted_supply_allocated_to_demand'),
  z.literal('inventory_supply_allocated_to_demand'),
]);
export type DemandColumn = z.infer<typeof demandColumns>;

const allocatedDemandTableLabel = z.union([
  z.literal('demandMatchingOverTimeByAllocated'),
  z.literal('demandMatchingAverageByAllocated'),
  z.literal('demandAllocationDetails'),
]);
const allocatedDemandBreakdownColumns = z.union([
  z.literal('allocated_production_device_technology'),
  z.literal('allocated_production_device_client_reference'),
  z.literal('allocated_production_device_name'),
  z.literal('allocated_production_device_country'),
  z.literal('allocated_production_device_sub_national_region'),
  z.string().regex(new RegExp(`^${ALLOCATED_SUPPLY_TAG_PREFIX}.+$`)),
]);

const allocatedDemandColumns = z
  .union([
    z.literal('period_start'),
    z.literal('supply_allocated_to_demand'),
    z.literal('supply_allocated_matching_demand'),
    z.literal('supply_allocated_in_excess_of_demand'),
    z.literal('allocated_production_device_latitude'),
    z.literal('allocated_production_device_longitude'),
  ])
  .or(allocatedDemandBreakdownColumns);

export type AllocatedDemandColumn = z.infer<typeof allocatedDemandColumns>;

const demandTableRequest = createMetricsTableRequest(demandColumns, demandTableLabel);
const demandTableResponse = createMetricsTableResponse(demandColumns, demandTableLabel);

const allocatedDemandTableRequest = createMetricsTableRequest(allocatedDemandColumns, allocatedDemandTableLabel);
const allocatedDemandTableResponse = createMetricsTableResponse(allocatedDemandColumns, allocatedDemandTableLabel);

export type DemandView = z.infer<typeof demandTableResponse>['views'][number];
export type DemandViewColumns = z.infer<typeof demandTableResponse>['views'][number]['columns'];
export type AllocatedDemandView = z.infer<typeof allocatedDemandTableResponse>['views'][number];

const combinedDemandColumns = z.union([...demandColumns.options, ...allocatedDemandColumns.options]);
const demandMatchingAndPlottingResolutionGroups = createMatchingAndPlottingResolutionGroups(combinedDemandColumns);
export type DemandGroups = z.infer<typeof demandMatchingAndPlottingResolutionGroups>;

// Demand-view request & response
export const demandRequestSchema = z.object({
  filters: demandFilters,
  tables: z.object({
    allocated_demand: allocatedDemandTableRequest.optional(),
    demand: demandTableRequest,
  }),
});

export type DemandRequest = z.infer<typeof demandRequestSchema>;

export const demandResponseSchema = z.object({
  has_extended_generation_period: z.boolean().optional(),
  tables: z.object({
    allocated_demand: allocatedDemandTableResponse.optional(),
    demand: demandTableResponse,
  }),
});

export type DemandResponse = z.infer<typeof demandResponseSchema>;

const demandGridDataSchema = z.object({
  certificate_label: z.array(z.string()).nullable().optional(),
  certificate_type: z.string().nullable().optional(),
  counterparty_client_reference: z.string().nullable().optional(),
  counterparty_name: z.string().nullable().optional(),
  country: z.array(countryEnum).nullable().optional(),
  deal_client_reference: z.string().nullable().optional(),
  deal_tranche_client_reference: z.string().nullable().optional(),
  deal_tranche_volume_type: dealTrancheVolumeTypeEnum.nullable().optional(),
  delivered_quantity: zodBigNumber.nullable().optional(),
  demand_contract_type: z.string().nullable().optional(),
  demand_quantity: zodBigNumber.nullable().optional(),
  generation_period_start_month: z.number().int().nullable().optional(),
  generation_period_start_year: z.number().int().nullable().optional(),
  max_delivery_month: z.number().int().nullable().optional(),
  max_delivery_year: z.number().int().nullable().optional(),
  minimum_commercial_operation_date_month: z.number().int().nullable().optional(),
  production_device_client_reference: z.string().nullable().optional(),
  production_device_id: z.uuid().nullable().optional(),
  production_device_name: z.string().nullable().optional(),
  production_device_registry_identifier: z.string().nullable().optional(),
  minimum_commercial_operation_date_year: z.number().int().nullable().optional(),
  period_start_month: z.number().int().nullable().optional(),
  period_start_year: z.number().int().nullable().optional(),
  quantity_allocated: zodBigNumber.nullable().optional(),
  quantity_allocated_from_contracted: zodBigNumber.nullable().optional(),
  quantity_allocated_from_inventory: zodBigNumber.nullable().optional(),
  sub_national_region: z.array(z.string()).nullable().optional(),
  subsidy_regime: z.array(subsidyRegimeEnum).nullable().optional(),
  supra_national_region: z.array(z.string()).nullable().optional(),
  technology: z.array(technologyEnum).nullable().optional(),
});

export type DemandGridMetricsData = z.infer<typeof demandGridDataSchema>;
export type DemandGridMetricsDataKeys = keyof DemandGridMetricsData;

export const demandMatchingResolutionsRequestSchema = z.object({
  filters: demandFilters,
  groups: demandMatchingAndPlottingResolutionGroups,
});
export type DemandMatchingResolutionsRequest = z.infer<typeof demandMatchingResolutionsRequestSchema>;

export const demandMatchingResolutionsResponseSchema = z.object({
  matching_resolutions: z.array(matchingResolutionEnum),
});
export type DemandMatchingResolutionsResponse = z.infer<typeof demandMatchingResolutionsResponseSchema>;

// Plotting resolution request
export const demandPlottingResolutionsRequestSchema = z.object({
  filters: demandFilters,
  groups: demandMatchingAndPlottingResolutionGroups,
  matching_resolution: matchingResolutionEnum,
});

export type DemandPlottingResolutionsRequest = z.infer<typeof demandPlottingResolutionsRequestSchema>;

export const carbonMetricSchema = z.object({
  error: carbonContentErrorEnum.optional(),
  value: z.number().optional(),
});

export type CarbonMetric = z.infer<typeof carbonMetricSchema>;

// Carbon Response Schemas
export const carbonMetricsSchema = z.object({
  location_based: carbonMetricSchema,
  market_based: carbonMetricSchema,
  unit: carbonContentUnitEnum.optional(),
});

export type CarbonMetrics = z.infer<typeof carbonMetricsSchema>;

export const demandCarbonRequestSchema = z.object({
  filters: demandFilters,
  matching_resolutions: z.array(matchingResolutionEnum),
});
export type DemandCarbonRequest = z.infer<typeof demandCarbonRequestSchema>;

export const demandCarbonResponseSchema = z.partialRecord(matchingResolutionEnum, carbonMetricsSchema);
export type DemandCarbonResponse = z.infer<typeof demandCarbonResponseSchema>;

export const demandPivotTableResponseSchema = gridData(
  z.object({
    count: z.number(),
    data: z.array(demandGridDataSchema.loose()), // loose to allow for tags
  }),
);
